import { clearPersistedQueryCache, clearStoredFarmLocations, getLocalKlimVar } from "utils";

// Impersonation state lives in sessionStorage (per-tab), so it never touches the admin's own
// localStorage token. Two keys: a mode flag and the imp_ token.
const IMPERSONATION_MODE_KEY = "IMPERSONATION_MODE";
const IMPERSONATION_TOKEN_KEY = "IMPERSONATION_TOKEN";

// One-time handover code, held in memory until exchanged. Lost on refresh on purpose.
let pendingCode: string | null = null;

const safeSession = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

const writeSession = (fn: () => void): void => {
  try {
    fn();
  } catch {
    // sessionStorage unavailable, ignore
  }
};

export const isImpersonating = (): boolean => safeSession(() => sessionStorage.getItem(IMPERSONATION_MODE_KEY) === "true", false);

export const getImpersonationToken = (): string | null => safeSession(() => sessionStorage.getItem(IMPERSONATION_TOKEN_KEY), null);

export const setImpersonationToken = (token: string): void => {
  writeSession(() => sessionStorage.setItem(IMPERSONATION_TOKEN_KEY, token));
};

// The token axios sends. While impersonating it's the imp_ token and NEVER the admin's localStorage token.
export const getActiveToken = (): string | null => (isImpersonating() ? getImpersonationToken() : getLocalKlimVar("token"));

export const getPendingImpersonationCode = (): string | null => pendingCode;

// Read a one-time code from the URL fragment (#code=). The fragment.
export const readImpersonationCodeFromHash = (): string | null => {
  const match = window.location.hash.match(/[#&]code=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const bootstrapImpersonationFromUrl = (): void => {
  // Only the /impersonate landing may start a session. ignore a stray/crafted #code= anywhere else.
  if (window.location.pathname !== "/impersonate") return;

  const code = readImpersonationCodeFromHash();
  if (!code) return;

  pendingCode = code;
  writeSession(() => sessionStorage.setItem(IMPERSONATION_MODE_KEY, "true"));
  window.history.replaceState(null, "", "/home");
};

export const bailOutOfImpersonation = async (options?: { closeTab?: boolean }): Promise<void> => {
  pendingCode = null;
  writeSession(() => {
    sessionStorage.removeItem(IMPERSONATION_MODE_KEY);
    sessionStorage.removeItem(IMPERSONATION_TOKEN_KEY);
  });

  try {
    clearStoredFarmLocations();
    await clearPersistedQueryCache();
  } catch {
    // redirect so the admin is never stuck in the farmer app
  }

  if (options?.closeTab) {
    window.close();
    // If the tab did not close (not script-opened), redirect so the user isn't stranded. window.close()
    // destroys this context when it works, so this fallback never runs for the normal new-tab flow.
    window.setTimeout(() => window.location.replace(process.env.REACT_APP_ADMIN_URL || "/"), 300);
    return;
  }

  window.location.replace(process.env.REACT_APP_ADMIN_URL || "/");
};
